import {
  AccessLevelOptions,
  DEFAULT_PERMISSIONS,
  IRoleDetails,
  PermissionActionValues,
  PermissionModules,
  PermissionType,
  RoleScopes
} from './constants'
import apiService from '../../services/api/apiService'
import { getStatusMessage } from '../../services/api/apiStatuses'
import { IUser } from '../users/usersSlice'
import { IGetUsersParams } from '../../interfaces'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

interface IManageRoleDetailsState {
  roleDetails: IRoleDetails
  usersList: {
    list: IUser[]
    total: number
  }
}

export type UpdateRolePermissionsParams = {
  permissionName: PermissionModules
  type?: PermissionType
  currentAccessLevel?: AccessLevelOptions
  currentActions?: PermissionActionValues[]
}

export const ACTION_FETCH_ROLE_DETAILS_BY_NAME = 'configureRoles/fetchRoleDetailsByName'

export const fetchRoleDetailsByName = createAsyncThunk(
  ACTION_FETCH_ROLE_DETAILS_BY_NAME,
  async (roleName: string) => {
    const response = await apiService.getRoleDetailsById(roleName)
    return response
  }
)

export const ACTION_UPDATE_ROLE_BY_NAME = 'configureRoles/updateRoleByName'

export const updateRoleByName = createAsyncThunk(
  ACTION_UPDATE_ROLE_BY_NAME,
  async (data: IRoleDetails, { rejectWithValue }) => {
    try {
      return await apiService.updateRoleById(data)
    } catch (error: any) {
      return rejectWithValue({
        statusMessage: getStatusMessage(error, 'settings.roles.update.error.message')
      })
    }
  }
)

export const ACTION_CREATE_CUSTOM_ROLE = 'configureRoles/createCustomRole'

export const createCustomRole = createAsyncThunk(
  ACTION_CREATE_CUSTOM_ROLE,
  async (data: IRoleDetails, { rejectWithValue }) => {
    try {
      return await apiService.createCustomRole(data)
    } catch (error: any) {
      return rejectWithValue({
        statusMessage: getStatusMessage(error, 'settings.roles.create.error.message')
      })
    }
  }
)

export const ACTION_ROLE_DETAILS_USERS_LIST = 'configureRoles/fetchRoleDetailsUsersList'

export const fetchRoleDetailsUsersList = createAsyncThunk(
  ACTION_ROLE_DETAILS_USERS_LIST,
  async (params: IGetUsersParams) => {
    const response = await apiService.getUsersList(params)
    return response
  }
)

const initialState: IManageRoleDetailsState = {
  roleDetails: { permissions: DEFAULT_PERMISSIONS },
  usersList: {
    list: [],
    total: 0
  }
}

const configureRolesSlice = createSlice({
  name: 'configureRoles',
  initialState,
  reducers: {
    updateRolePermission: (state, action) => {
      const {
        permissionName,
        type = PermissionType.RADIO,
        currentAccessLevel,
        currentActions = []
      }: UpdateRolePermissionsParams = action.payload
      if (state.roleDetails.permissions) {
        const permissionIndex = state.roleDetails.permissions.findIndex(
          (p) => p.permissionName === permissionName
        )
        if (permissionIndex !== -1) {
          if (type === PermissionType.RADIO) {
            state.roleDetails.permissions[permissionIndex].currentAccessLevel = currentAccessLevel
          }
          if (type === PermissionType.CHECKBOX) {
            state.roleDetails.permissions[permissionIndex].currentActions = currentActions
          }
        }
      }
    },
    updateRoleMetadataDetails: (state, action) => {
      state.roleDetails = {
        ...state.roleDetails,
        ...action.payload
      }
    },
    resetRoleDetails: (state) => {
      state.roleDetails = initialState.roleDetails
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRoleDetailsByName.fulfilled, (state, action) => {
      const { payload } = action
      let permissions = payload.permissions
      if (permissions && payload.scope === RoleScopes.CUSTOM) {
        const existingNames = new Set(permissions.map((p) => p.permissionName))
        const missingDefaults = DEFAULT_PERMISSIONS.filter(
          (d) => !existingNames.has(d.permissionName)
        )
        permissions = [...permissions, ...missingDefaults]
      }
      state.roleDetails = { ...payload, permissions }
    })
    builder.addCase(fetchRoleDetailsUsersList.fulfilled, (state, action) => {
      state.usersList = {
        list: action.payload.list,
        total: action.payload.total
      }
    })
  }
})

export const {
  updateRolePermission,
  updateRoleMetadataDetails,
  resetRoleDetails
} = configureRolesSlice.actions

export default configureRolesSlice.reducer
